import React, { createContext, memo, useEffect, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { useLocation } from 'react-router-dom';
import { CommitteeDepartment } from '@shared/enums/CommitteeDepartment';
import { Branding } from '@shared/enums/Branding';
import { BASE_API_URL } from '../../constants/api';
import { apiInstance } from '../components/OnboardingForm/utils';
import { EUserRole } from '../enums/user-role';
import { EUserCapability } from '../enums/user-capability';
import { Entity } from './entity';

type GetOption = {
    (key: string, group: string, defaultValue?: any): any;
};

type HasCapability = {
    (EUserCapability): boolean;
};

export type User = {
    id: string;
    role: EUserRole[];
    capabilities: EUserCapability[];
    isHRP: boolean;
    fullName: string;
    clientType: 'counterparty' | 'vendor';
    slackProfileId: string | null;
    clients: Entity[];
    activeCounterparty: Entity | Record<string, never>;
    zendeskUserId: string | null;
    email?: string;
    useOrganizations?: boolean;
    department: CommitteeDepartment[];
    branding: Branding;
};

type IUserContext = {
    error: object | undefined;
    user: User;
    loading: boolean;
    getOption: GetOption;
    hasCapability: HasCapability;
    getBrandingText: () => string;
};

const initialValue: IUserContext = {
    error: {},
    user: {
        role: [],
        isHRP: false,
        id: '',
        capabilities: [],
        fullName: '',
        clientType: 'counterparty',
        slackProfileId: '',
        clients: [],
        activeCounterparty: {},
        zendeskUserId: null,
        email: '',
        useOrganizations: false,
        department: [],
        branding: Branding.HRP,
    },
    loading: true,
    getOption: (): void => {},
    hasCapability: () => false,
    getBrandingText: () => 'Hidden Road',
};

const UserContext = createContext(initialValue);

function UserContextProvider(props) {
    const location = useLocation();
    const { data, error, loading, run } = useRequest(
        `${BASE_API_URL}/auth/me`,
        {
            requestMethod: (param) => apiInstance(param),
            manual: true,
            refreshDeps: [location.pathname],
        },
    );

    const getOption = (key: string, group = null, defaultValue = null) => {
        if (data) {
            const option = data.data.options.find((op) => {
                if (group) {
                    return op.key === key && op.group === group;
                }
                return op.key === key;
            });
            if (option) {
                return option.value;
            }
        }

        return defaultValue;
    };

    const hasCapability = (capability: EUserCapability) =>
        data?.data?.capabilities.includes(capability);

    const getBrandingText = () =>
        data?.data?.branding === Branding.HRP ? 'Hidden Road' : 'Ripple';

    useEffect(() => {
        run();
    }, [location]);

    return useMemo(
        () =>
            data || error ? (
                <UserContext.Provider
                    {...props}
                    value={{
                        user: data?.data,
                        error,
                        loading,
                        getOption,
                        hasCapability,
                        getBrandingText,
                    }}
                />
            ) : null,
        [location, data?.status, error],
    );
}

const memoizedUserContext = memo(UserContextProvider);

export default UserContext;
export { memoizedUserContext as UserContextProvider };
